import type { SendBiAgentMessageRequest } from "./types/biAgentTypes";

export type UserInputContract<T, K extends keyof T> = Omit<T, K> & {
  [P in K as `${P & string}UserInput`]: T[P];
};

export const toUserInputContract = <T extends object, K extends keyof T & string>(
  value: T,
  keys: readonly K[]
): UserInputContract<T, K> =>
  Object.fromEntries(
    Object.entries(value).map(([key, item]) => [keys.some((name) => name === key) ? `${key}UserInput` : key, item])
  ) as UserInputContract<T, K>;

export const toBuildReportContract = <T extends object>(value: T): object => {
  const keys = [
    "conditions",
    "fields",
    "rows",
    "columns",
    "values",
    "arguments",
    "settings",
    "sort",
    "numberFormat",
    "breakdownBatches",
    "lines",
  ];
  return Object.fromEntries(
    Object.entries(value).map(([key, item]) => [keys.includes(key) ? `${key}UserInput` : key, item])
  );
};

export const toRunReportContract = <T extends object>(value: T): object => {
  const keys = ["conditions", "sort", "settings"];
  return Object.fromEntries(
    Object.entries(value).map(([key, item]) => [keys.includes(key) ? `${key}UserInput` : key, item])
  );
};

export const toBiAgentMessageContract = (value: SendBiAgentMessageRequest): object => ({
  ...value,
  currentTabularConfiguration: value.currentTabularConfiguration
    ? toBuildReportContract(value.currentTabularConfiguration)
    : undefined,
  currentPivotConfiguration: value.currentPivotConfiguration
    ? toBuildReportContract(value.currentPivotConfiguration)
    : undefined,
});

export const toBuildExportContract = <T extends { exportConfig: unknown; buildConfig: object }>(value: T): object => {
  const { exportConfig, buildConfig, ...rest } = value;
  return { ...rest, exportConfigUserInput: exportConfig, buildConfig: toBuildReportContract(buildConfig) };
};

export const toRunExportContract = <T extends { exportConfig: unknown; viewConfig: { conditions?: unknown } }>(
  value: T
): object => {
  const { exportConfig, viewConfig, ...rest } = value;
  return { ...rest, exportConfigUserInput: exportConfig, viewConfig: toRunReportContract(viewConfig) };
};
